import {AxiosResponse} from 'axios'
import {RawPagedCollectionResponse} from '../types'
import {HttpInstance} from '../createHttpInstance'

/**
 * Division attendees — one row per person per division for a season.
 *
 * Path and response shape confirmed against the divisions service's own
 * generated spec (`divisions` repo, `gen/docs/external/openapi.yaml`, operation
 * `ListAttendees`) and a recorded QA baseline response for
 * `GET /v0/divisions/attendees`.
 *
 * A camper with no division assignment comes back with `DivisionID: 0` rather
 * than being omitted, so 0 must be read as "unassigned", not as a division id.
 */
interface Params {
  ClientID: number
  SeasonID: number
  /** Repeated once per id. Omitted entirely when empty. */
  DivisionIDs?: number[]
  /** Repeated once per id. Omitted entirely when empty. */
  PersonIDs?: number[]
  PageNumber: number
  PageSize: number
}

export interface RawDivisionAttendee {
  ID: number
  Self: string
  ClientID: number
  SeasonID: number
  PersonID: number
  DivisionID: number
}

export type ListDivisionAttendeesResponse =
  RawPagedCollectionResponse<RawDivisionAttendee>

export interface ListDivisionAttendees<P, R> {
  (http: HttpInstance, params: P): Promise<AxiosResponse<R>>
}

export const listDivisionAttendees: ListDivisionAttendees<
  Params,
  ListDivisionAttendeesResponse
> = (http, params) => {
  const {ClientID, SeasonID, DivisionIDs, PersonIDs, PageNumber, PageSize} =
    params

  const queryParams: string[] = []
  queryParams.push(`clientid=${ClientID}`)
  queryParams.push(`seasonid=${SeasonID}`)
  queryParams.push(`pagenumber=${PageNumber}`)
  queryParams.push(`pagesize=${PageSize}`)
  ;(DivisionIDs ?? []).forEach((id) => queryParams.push(`divisionids=${id}`))
  ;(PersonIDs ?? []).forEach((id) => queryParams.push(`personids=${id}`))

  const query = `?${queryParams.join('&')}`

  return http.get<ListDivisionAttendeesResponse>(
    `/v0/divisions/attendees${query}`,
  )
}
